import {
  inPageSurveyAnswerStatus,
  InPageSurveyAnswerStatus,
} from '../../common/libs/inPageSurveyAnswerStatus';
import {
  inPageSurveyQuestionType,
  InPageSurveyQuestionType,
} from '../../common/libs/inPageSurveyQuestionType';
import { InPageSurveyUiType } from '../../common/libs/inPageSurveyUiType';
import { InPageSurvey, SurveyQuestion } from '../../hooks/useInPageSurvey';
import prisma from './prisma';

/**
 * 指定のスライドに設置されているインページサーベイの情報を取得する
 *
 * @param articleId スライドID
 * @param userId ユーザーID
 * @returns インページサーベイ情報の配列
 */
export async function fetchInPageSurvey(
  articleId: string,
  userId: number,
): Promise<InPageSurvey[]> {
  // 対象のスライドに設置されているインページサーベイのIDを取得する
  const surveyIds = await prisma.article_has_in_page_surveys
    .findMany({
      where: {
        article_id: articleId,
      },
      select: {
        survey_id: true,
      },
    })
    .then((rows) => rows.map((row) => row.survey_id));
  if (surveyIds.length === 0) {
    return [];
  }

  // インページサーベイに関する情報を取得する
  const surveys = await prisma.in_page_surveys.findMany({
    where: {
      id: {
        in: surveyIds,
      },
      is_deleted: false,
    },
    select: {
      id: true,
      ui_type: true,
      title: true,
      logo_img_url: true,
      questions: {
        where: {
          is_deleted: false,
        },
        select: {
          question_number: true,
          question_type: true,
          question: true,
          insert_position: true,
          choices: {
            select: {
              choice_number: true,
              choice_name: true,
            },
            orderBy: {
              choice_number: 'asc',
            },
          },
          answers: {
            where: {
              user_id: userId,
            },
            select: {
              answer_status: true,
            },
          },
        },
        orderBy: [
          {
            question_number: 'asc',
          },
          {
            insert_position: 'asc',
          },
        ],
      },
    },
  });

  if (surveys.length === 0) {
    return [];
  }

  return surveys.map((survey) => ({
    surveyId: survey.id,
    uiType: survey.ui_type as InPageSurveyUiType,
    title: survey.title ?? null,
    logoImgUrl: survey.logo_img_url ?? null,
    numberOfQuestions: survey.questions.length,
    questions: survey.questions.map((q) => ({
      questionNumber: q.question_number,
      questionType: q.question_type as InPageSurveyQuestionType,
      question: q.question,
      insertPosition: q.insert_position as number,
      choices:
        (q.question_type === inPageSurveyQuestionType.MULTIPLE_CHOICE ||
          q.question_type === inPageSurveyQuestionType.SINGLE_CHOICE) &&
        q.choices.length > 0
          ? q.choices.map((c) => ({
              choiceNumber: c.choice_number,
              choiceName: c.choice_name,
            }))
          : null,
      answerStatus:
        q.answers.length > 0
          ? (q.answers[0]?.answer_status as InPageSurveyAnswerStatus)
          : null, // user_idで絞り込んでいるため、要素が複数になることはない
    })),
  }));
}

export type PageSurveyQuestionWithArticlePage = {
  survey: InPageSurvey;
  question: SurveyQuestion;
};

/**
 * スライドのページ番号に対応するインページサーベイの質問を取得する
 *
 * @param inPageSurveys インページサーベイの配列
 * @param pageNumber スライドのページ番号
 * @returns インページサーベイと質問の組、またはnull（該当する質問がない場合）
 */
export function getPageSurveyQuestionWithArticlePage({
  inPageSurveys,
  pageNumber,
}: {
  inPageSurveys: InPageSurvey[] | null;
  pageNumber: number;
}): PageSurveyQuestionWithArticlePage[] | null {
  if (!inPageSurveys) {
    return null;
  }

  const result: PageSurveyQuestionWithArticlePage[] = [];

  for (const survey of inPageSurveys) {
    for (const question of survey.questions) {
      if (
        question.insertPosition === pageNumber &&
        question.answerStatus !== inPageSurveyAnswerStatus.DONE &&
        question.answerStatus !== inPageSurveyAnswerStatus.SKIP
      ) {
        result.push({ survey, question });
      }
    }
  }

  return result.length > 0 ? result : null;
}
